<script>
    import { tick } from "svelte";
    import { slide } from "svelte/transition";
    /**
     * The component expects a `merch` object with this structure:
     * {
     *   "id": 1,
     *   "name": "Django T-Shirt",
     *   "variations": [
     *     {
     *       "type_name": "Size",
     *       "options": [{ "id": 1, "value": "S" }, { "id": 2, "value": "M" }]
     *     },
     *     {
     *       "type_name": "Color",
     *       "options": [{ "id": 5, "value": "Blue" }, { "id": 6, "value": "Red" }]
     *     }
     *   ]
     * }
     */
    export let merch;

    let message = "";
    let email = "";
    // Use an object to store the selected ID for each variation type.
    // e.g., { "Size": 1, "Color": 6 }
    let selectedVariations = {};

    let sending = false;
    let statusMessage = "";
    let statusType = "success"; // can be 'success' or 'error'

    async function sendRequest() {
        // Basic validation: ensure all dropdowns are selected
        if (
            Object.keys(selectedVariations).length !== merch.variations.length
        ) {
            statusType = "error";
            statusMessage = "Please select an option for each variation.";
            return;
        }

        sending = true;
        statusMessage = "";

        const payload = {
            merch_id: merch.id,
            message: message || "Standard request.", // Default message if empty
            variation_ids: Object.values(selectedVariations).map((id) =>
                parseInt(id),
            ),
            email: email,
        };

        try {
            const response = await fetch(
                "/api/merch/request",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                },
            );

            if (response.ok) {
                statusType = "success";
                statusMessage = "Έφυγε επιτυχώς!";
                // Reset form
                message = "";
                email = "";
                selectedVariations = {};
                // Force Svelte to re-render the selects to their default state
                await tick();
            } else {
                const errorData = await response.json();
                statusType = "error";
                statusMessage = `SNAFU: ${errorData.message || "Κάτι πήγε στραβά."}`;
            }
        } catch (error) {
            statusType = "error";
            statusMessage = "Το δίκτυο τα έπαιξε, προσπάθησε πάλι.";
        } finally {
            sending = false;
        }
    }
</script>

<!-- Status Message Display -->
{#if statusMessage}
    <div
        class="alert mt-4 {statusType === 'success'
            ? 'border-2 border-green-400  text-primary-content'
            : 'border-2 border-red-400  text-primary-content'}"
    >
        {statusMessage}
    </div>
{/if}

<!-- Form Structure -->
<form
    transition:slide
    on:submit|preventDefault={sendRequest}
    class="opacity-70 grid grid-cols-5 gap-1 mb-44 mt-4"
>
    <label
        class="hidden xl:block p-1 bg-neutral shadow-md shadow-black text-primary-content border-2 border-primary-neutral"
        for="email"
    >
        <span class="label-text">Email </span>
    </label>
    <input
        id="email"
        type="email"
        placeholder="glowie@cia.gov"
        class="col-span-3 p-1 bg-neutral shadow-md shadow-black text-primary-content-content border-2 border-primary-neutral focus:border-fuchsia-400"
        bind:value={email}
    />

    <!-- Submit Button -->
    <button
        type="submit"
        class="btn bg-primary-content text-primary shadow-md shadow-black"
        disabled={sending}
    >
        {#if sending}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                style="margin: auto; background: rgb(255, 255, 255); display: block; shape-rendering: auto;"
                width="24px"
                height="24px"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid"
            >
                <path
                    fill="none"
                    stroke="#d946ef"
                    stroke-width="8"
                    stroke-dasharray="177.0463604736328 79.54256774902345"
                    d="M24.3 30C11.4 30 5 43.3 5 50s6.4 20 19.3 20c19.3 0 32.1-40 51.4-40 C88.6 30 95 43.3 95 50s-6.4 20-19.3 20C56.4 70 43.6 30 24.3 30z"
                    stroke-linecap="round"
                    style="transform:scale(1);transform-origin:50px 50px"
                >
                    <animate
                        attributeName="stroke-dashoffset"
                        repeatCount="indefinite"
                        dur="1s"
                        keyTimes="0;1"
                        values="0;256.58892822265625"
                    />
                </path>
            </svg>
        {:else}
            Send Request
        {/if}
    </button>

    <!-- Dynamically create dropdowns for each variation type -->
    {#each merch.variations as variationGroup}
        <label
            class="hidden xl:block p-1 bg-neutral shadow-md shadow-black text-primary-content border-2 border-primary-neutral"
            for="variation-{variationGroup.type_name}"
        >
            <span class="label-text">{variationGroup.type_name}</span>
        </label>
        <select
            id="variation-{variationGroup.type_name}"
            required
            class="col-span-4 p-1 bg-neutral shadow-md shadow-black text-primary-content-content border-2 border-primary-neutral focus:border-fuchsia-400"
            bind:value={selectedVariations[variationGroup.type_name]}
        >
            <option disabled selected value={undefined}>Select an option</option
            >
            {#each variationGroup.options as option}
                <option value={option.id}>{option.value}</option>
            {/each}
        </select>
    {/each}

    <!-- Message Textarea -->
    <label
        class="hidden xl:block p-1 bg-neutral shadow-md shadow-black text-primary-content border-2 border-primary-neutral"
        for="message"
    >
        <span class="label-text">Message (Optional)</span>
    </label>
    <textarea
        id="message"
        class="col-span-4 p-1 bg-neutral shadow-md shadow-black text-primary-content border-2 border-primary-neutral focus:border-fuchsia-400"
        placeholder="Any additional details..."
        bind:value={message}
    />
</form>
